/**
 * Centralized user-facing copy for the Batches sub-feature (Mail-on-Demand
 * print-shop admin).
 *
 * This repo has no dedicated i18n framework yet, so — per the project rule
 * that feature text must not be hardcoded inline — every label/message any
 * Batches component renders (list page, builder page, ship dialog, manifest
 * download) is sourced from this single nested `as const` object and
 * interpolated with the shared `formatMessage` helper exported from the
 * parent `printShop/strings.ts` (imported directly by consumers, not
 * re-exported here — same convention `pricingConfigStrings` follows for its
 * own sub-feature). When a real i18n library is adopted repo-wide, this
 * object is the natural seed for the default-locale catalog.
 */
export const batchesStrings = {
  list: {
    pageTitle: 'Print Shop Batches',
    newBatchButton: '+ New batch',
    refreshButton: 'Refresh',
    tabAll: 'All',
    tabOpen: 'Open',
    tabShipped: 'Shipped',
    searchPlaceholder: 'Search member or order id…',
    columns: {
      batch: 'Batch',
      status: 'Status',
      packages: 'Packages',
      orders: 'Orders',
      created: 'Created',
      shipped: 'Shipped',
      trackingRef: 'Tracking',
    },
    packageProgress: '{count}/{target}',
    noTracking: '—',
    empty: 'No batches found for this filter.',
    loadError: 'Could not load batches. Please try again.',
    perPage: '{limit} per page',
    pageIndicator: 'Page {currentPage} of {lastPage}',
    prev: 'Prev',
    next: 'Next',
  },
  builder: {
    pageTitle: 'Batch {batchId}',
    newPageTitle: 'New batch',
    backToBatches: 'Back to batches',
    loading: 'Loading batch…',
    notFound: 'Batch not found.',
    loadError: 'Could not load this batch. Please try again.',
    copyFullId: 'Copy full id',
    copiedId: 'Copied!',
    // Surfaced next to the created date once a batch has shipped (status
    // !== 'open'): mirrors TicketDetail's "{status} — no actions" terminal
    // copy pattern, plus the tracking reference when one was recorded.
    shippedMeta: 'Shipped {date}',
    shippedMetaWithTracking: 'Shipped {date} · Tracking {trackingRef}',
    summary: {
      packageProgress: '{count}/{target} packages',
      orderCount: '{count} orders',
      targetHint: '/ {target} target',
      // Static replacement for the "Ship batch" button once a batch is no
      // longer open — same "{status} — no actions" terminal-state pattern
      // TicketDetail.tsx uses for its own hero action slot.
      shippedBadge: 'Shipped — no actions',
    },
    candidates: {
      sectionTitle: 'Eligible orders',
      searchPlaceholder: 'Search member or order id…',
      selectedSummary: '{count} selected · {packages} packages',
      addButton: 'Add to batch',
      addingButton: 'Adding…',
      empty: 'No eligible orders to add right now.',
      loadError: 'Could not load eligible orders. Please try again.',
      eligibilityNote:
        "Only orders that have been printed and aren't already in another open batch are eligible here.",
      addSuccess: '{count} order(s) added to the batch.',
      addPartialSuccess:
        '{added} order(s) added; {skipped} order(s) were skipped because they were already in another batch.',
      addError: 'Could not add the selected orders. Please try again.',
    },
    orders: {
      sectionTitle: 'In this batch',
      empty: 'No orders in this batch yet.',
      removeButton: '✕ Remove',
      viewButton: 'View details',
      removeConfirmTitle: 'Remove order from batch',
      removeConfirmMessage: 'Remove order {orderId} from this batch?',
      removeConfirmButton: 'Remove',
      removeSuccess: 'Order {orderId} removed from the batch.',
      removeError: 'Could not remove order {orderId}. Please try again.',
      removeSelectedButton: 'Remove selected ({count})',
      removeSelectedConfirmTitle: 'Remove selected orders',
      removeSelectedConfirmMessage: 'Remove {count} order(s) from this batch?',
      removeSelectedSuccess: '{count} order(s) removed from the batch.',
      removeSelectedPartialSuccess:
        '{removed} order(s) removed; {skipped} order(s) were skipped (already removed or not a member).',
      removeSelectedError: 'Could not remove the selected orders. Please try again.',
    },
    columns: {
      order: 'Order',
      member: 'Member',
      asset: 'Asset',
      packages: 'Packages',
      printedAt: 'Printed',
      actions: 'Actions',
    },
  },
  manifest: {
    downloadButton: '⬇ Download CSV manifest',
    downloadError: 'Could not download the manifest CSV. Please try again.',
  },
  ship: {
    button: 'Ship batch',
    dialogTitle: 'Ship batch {batchId}',
    trackingLabel: 'Tracking reference',
    trackingPlaceholder: 'e.g. 1Z999AA10123456784',
    trackingHint:
      'Leaving this empty asks "Ship without tracking?" before confirming.',
    noTrackingTitle: 'Ship without tracking?',
    noTrackingMessage:
      'No tracking reference was entered for batch {batchId}. Ship it anyway?',
    noTrackingConfirm: 'Ship without tracking',
    confirmButton: 'Ship',
    cancelButton: 'Cancel',
    success: 'Batch {batchId} shipped.',
    error: 'Could not ship batch {batchId}. Please try again.',
  },
} as const;
